import React from "react";
import { CircularProgress, FormControlLabel, Radio, RadioGroup, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { getIn } from "formik";
import dayjs from "dayjs";
import NumberFormat from "react-number-format";
import { useCSVReader } from "react-papaparse";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { goldDatePickerTheme } from "../../../themes";
import { DayEnum, WhereToRedeemEnum } from "../../../graphql/types";
import { setDataAfterCSVImport, setPrivateEmails } from "../../common/PrivatePerk";
import CmpPerkBannerSection from "./CmpPerkBannerSection";
import { CMP, useCmpPerkFormStyles } from "./cmpPerkFormStyles";
import IdaMascot from "./cmp-ida-mascot.png";

/**
 * CMP Accelerator Member Dashboard design, screen "Create Company Perks"
 * (CMP Accelerator.dc.html). Every visible string below is copied from that
 * screen. Rendered by CreateMemberPerk only for CMP member logins
 * (useMemberDashboardAccess().access.cmpEnabled); CPA-only members and staff
 * keep the existing layout.
 */
export const CMP_PERK_FORM_COPY = {
    titleCreate: "Create Company Perks",
    titleUpdate: "Update Perk",
    forText: "for",
    subtitle: "Create your company perk on our Chamber Perks App™ and website directories. Watch video or view instructions to learn how.",
    perkDescription: "Perk Description",
    perkDescriptionHelper1: "Use our Intelligent Digital Assistant (IDA) to generate a clear and compelling perk description.",
    perkDescriptionHelper2: "Simply enter a few key details about your perk offer, and IDA will transform your input into a perk description designed to capture attention and encourage users to redeem your offer",
    perkDescriptionPlaceholder: "Enter Perk Details",
    perkTitle: "Perk Title",
    perkTitleHelper1: "Use our Intelligent Digital Assistant (IDA) to generate a catchy perk title that grabs attention and encourages users to open and view the full details in your description.",
    perkTitleHelper2: "Simply enter a few key details about your offer, and IDA will create a title designed to stand out and drive engagement.",
    perkTitlePlaceholder: "Enter Perk Title",
    characters: "characters",
    generate: "Generate",
    comingSoon: "COMING SOON",
    redeemableBy: "Perk Redeemable By",
    redeemableByHelperBefore: "Select one of the options below to choose who can view and redeem this perk. To maximize visibility and reach, select ",
    redeemableByHelperBold: "Anyone",
    anyone: "Anyone (",
    recommended: "Recommended",
    anyoneDesc: "This perk can be viewed and redeemed by any user using the app.",
    localMembers: "Local Members Only",
    localMembersDesc: "This perk can be viewed and redeemed only by companies and their employees who are members of your local Chamber Commerce or Board of Trade",
    allMembers: "All Members",
    allMembersDesc: "This perk can be viewed and redeemed by members and their employees from any participating Chamber or Board of Trade.",
    employeesOnly: "Your Company Employees Only",
    employeesOnlyDesc: "This perk can be viewed and redeemed only by the employees authorized from the company. (Employees MUST download the Chamber Perks App™ and share their email used to set up their personal user account to be able to view and redeem.",
    companiesEmployeeOnly: "Companies Employee ONLY",
    howToSetUp: "How to Set Up",
    option1: "Option 1:",
    option1Text1: "Have employees download the Chamber Perks App™ and create their personal user account, have them provide the email they used.",
    option2: "Option 2:",
    option2Text2: "To view the required format, click the sample CSV link below the Import CSV button.",
    importCsv: "Import CSV",
    downloadSample: "Download Import CSV Sample",
    enterEmployeesEmail: "Enter Employees Email",
    importantNote: "IMPORTANT NOTE: EMAILS CAN BE REMOVED AND EMPLOYEE WILL NO LONGER BE ABLE TO VIEW OR REDEEM COMPANY EMPLOYEE PERKS.",
    importantNoteBefore: "To remove an email address, ",
    importantNoteBold: "click the X beside it.",
    availability: "Perk Redemption Availability",
    availabilityHelper: "Choose a recurring perk to reward and retain loyal customers with benefits they can redeem multiple times, while a one-time perk is designed to attract new customers by offering a single, exclusive incentive for initial engagement.",
    recurring: "Recurring Perk Redemption",
    recurringDesc: "Users can redeem this perk multiple times.",
    oneTime: "One Time Perk Redemption",
    oneTimeDesc: "Users can redeem this perk only once.",
    certainDays: "Only Offer Perk on Certain Days",
    certainDaysHelper: "Use this option to make the perk available only on selected dates, helping control when the offer can be redeemed and align it with special promotions or company operations.",
    schedule: "Schedule Perk Display Date",
    scheduleHelper: "Use this option to set a start and end date for the perk, controlling when the offer is visible and available for redemption in the app. This is useful for managing promotional periods and time-limited offers.",
    startDate: "Start Date",
    endDate: "End Date",
    clickToSetDate: "Click to set the date",
    notificationNote: "A notification will not be sent for this perk because the current time does not match the availability day(s) and/or month(s) selected above.",
    whereToRedeem: "Where to Redeem Perk",
    whereToRedeemHelper: "Select where and how users can redeem this perk.",
    inPerson: "In Person",
    inPersonDesc: "Select this option if users must visit the company location to redeem this perk.",
    online: "Online",
    onlineDesc: "Select this option if users can redeem this perk online through a website or online store.",
    contactCompany: "Contact Company",
    contactCompanyDesc: "Select this option if users must contact the company directly (by phone, email, or message) to redeem perk.",
    forOnlineBefore: "For ",
    forOnlineAfter: " Perk Redemptions",
    forContactAfter: " Perk Redemption",
    discountCode: "Discount Code",
    discountCodePlaceholder: "Enter your Discount Code",
    ecommerceUrl: "Company E-commerce Website URL",
    ecommerceUrlPlaceholder: "Enter Website URL",
    contactName: "Department or Contact Name",
    contactNamePlaceholder: "Enter Department or Contact Name",
    contactEmail: "Company Contact Email",
    contactEmailPlaceholder: "Enter Company Contact Email",
    contactNumber: "Contact Number",
    contactNumberPlaceholder: "Enter Contact Number",
    sendNotification: "Send New Perk Notification",
    sendNotificationHelper: "Keep this enabled so users can be notified when a new perk is created.",
    createPerk: "Create Perk",
    updatePerk: "Update Perk",
    // Banner footer bold word: the design (featuredOn block) says "Update";
    // create mode names this page's button instead (agreed deviation).
    bannerFooterActionUpdate: "Update",
};

const C = CMP_PERK_FORM_COPY;

const DAYS = [
    { label: "Sun", value: DayEnum.Sunday },
    { label: "Mon", value: DayEnum.Monday },
    { label: "Tue", value: DayEnum.Tuesday },
    { label: "Wed", value: DayEnum.Wednesday },
    { label: "Thurs", value: DayEnum.Thursday },
    { label: "Fri", value: DayEnum.Friday },
    { label: "Sat", value: DayEnum.Saturday },
];

const RadioDot = ({ checked }: { checked: boolean }) => (
    <span
        style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: checked ? `5px solid ${CMP.gold500}` : `2px solid ${CMP.gray400}`,
            flexShrink: 0,
            boxSizing: "border-box",
            display: "inline-block",
            background: CMP.white,
        }}
    />
);

const CheckMark = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={CMP.green500} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
);

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={CMP.text} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
);

export interface CmpPerkFormProps {
    mode: "create" | "update";
    formik: any;
    memberName?: string | null;
    isSandbox: boolean;
    isFeaturedMember: boolean;
    loader: boolean;
    idaGeneratedTitle: boolean;
    idaGeneratedDescription: boolean;
    idaTitleLabel: string;
    idaDescriptionLabel: string;
    onTitleTyped: () => void;
    onDescriptionTyped: () => void;
    onGenerate: (field: "title" | "description") => void;
    onDayToggle: (day: DayEnum) => void;
    showFutureStartDateNotification: boolean;
    downloadSample: () => void;
    /** Update mode: one notification per perk per day (existing rule). */
    notifyDisabled?: boolean;
    notifyNote?: string;
}

const CmpPerkForm = (props: CmpPerkFormProps): React.ReactElement => {
    const {
        mode, formik, memberName, isSandbox, isFeaturedMember, loader,
        idaGeneratedTitle, idaGeneratedDescription, idaTitleLabel, idaDescriptionLabel,
        onTitleTyped, onDescriptionTyped, onGenerate, onDayToggle, showFutureStartDateNotification, downloadSample,
        notifyDisabled = false, notifyNote,
    } = props;
    const classes = useCmpPerkFormStyles();
    const { CSVReader } = useCSVReader();
    const values = formik.values;
    const submitLabel = mode === "update" ? C.updatePerk : C.createPerk;
    const titleBase = mode === "update" ? C.titleUpdate : C.titleCreate;
    const bannerFooterAction = mode === "update" ? C.bannerFooterActionUpdate : C.createPerk;

    const showFieldError = (path: string) =>
        (formik.submitCount > 0 || getIn(formik.touched, path)) && getIn(formik.errors, path);
    const descriptionError = showFieldError("description");
    const titleError = showFieldError("title");
    const fieldBorder = (error: any, byIda: boolean) =>
        `1px solid ${error ? CMP.red : byIda ? CMP.green500 : CMP.gray400}`;

    const radio = (value: string, checked: boolean, title: React.ReactNode, desc: string) => (
        <FormControlLabel
            key={value}
            value={value}
            className={classes.radioLabel}
            control={<Radio disableRipple icon={<RadioDot checked={false} />} checkedIcon={<RadioDot checked />} />}
            label={
                <span className={classes.radioText}>
                    <span className={classes.radioTitle}>{title}</span>
                    <span className={classes.radioDesc}>{desc}</span>
                </span>
            }
            checked={checked}
        />
    );

    const toggle = (label: string, on: boolean, onChange: (next: boolean) => void, disabled = false) => (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            className={classes.toggle}
            disabled={disabled}
            onClick={() => onChange(!on)}
            style={{
                borderColor: on ? CMP.gold500 : CMP.gray400,
                background: on ? CMP.gold100 : CMP.white,
                justifyContent: on ? "flex-end" : "flex-start",
                ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            }}
        >
            <span className={classes.toggleKnob} style={{ background: on ? CMP.gold500 : CMP.black900 }} />
        </button>
    );

    const generateControl = (field: "title" | "description") => (
        <span className={classes.idaGroup}>
            <img src={IdaMascot} alt="IDA" className={classes.idaMascot} />
            {isSandbox ? (
                <button type="button" className={classes.generateButton} onClick={() => onGenerate(field)}>{C.generate}</button>
            ) : (
                <span className={classes.comingSoon}>{C.comingSoon}</span>
            )}
        </span>
    );

    const errorText = (path: string) => {
        const message = showFieldError(path);
        return message ? <div data-error="true" className={classes.fieldError}>{message}</div> : null;
    };

    const datePicker = (field: "startDate" | "endDate") => (
        <MuiThemeProvider theme={goldDatePickerTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                    value={values[field] ? dayjs(values[field]) : null}
                    minDate={field === "endDate" && values.startDate ? dayjs(values.startDate).add(1, "day") : undefined}
                    onChange={(newValue: any) => formik.setFieldValue(field, newValue ? newValue.format("YYYY-MM-DD") : "")}
                    format="MMM D, YYYY"
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: "outlined",
                            placeholder: C.clickToSetDate,
                            inputProps: { "aria-label": field === "startDate" ? C.startDate : C.endDate },
                            InputProps: { endAdornment: <CalendarIcon /> },
                            sx: {
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    background: CMP.white,
                                    padding: "0 16px 0 0",
                                    cursor: "pointer",
                                    "& fieldset": { borderColor: CMP.text, borderWidth: "1px" },
                                    "&:hover fieldset": { borderColor: CMP.text },
                                    "&.Mui-focused fieldset": { borderColor: CMP.gold500, borderWidth: "1px" },
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "13px 16px",
                                    fontSize: "14px",
                                    fontFamily: CMP.font,
                                    color: CMP.text,
                                    cursor: "pointer",
                                    "&::placeholder": { color: CMP.text, opacity: 1 },
                                },
                            },
                        },
                        dialog: {
                            sx: {
                                "& .MuiPickersDay-root.Mui-selected": { backgroundColor: `${CMP.gold500} !important` },
                                "& .MuiPickersDay-root:hover": { backgroundColor: "rgba(225, 167, 49, 0.1)" },
                                "& .MuiPickersDay-today": { borderColor: `${CMP.gold500} !important` },
                                "& .MuiButton-textPrimary": { color: `${CMP.gold500} !important` },
                                "& .MuiPickersYear-yearButton.Mui-selected": { backgroundColor: `${CMP.gold500} !important` },
                                "& .MuiPickersMonth-monthButton.Mui-selected": { backgroundColor: `${CMP.gold500} !important` },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
        </MuiThemeProvider>
    );

    const redeemInput = (path: string, placeholder: string, label: string) => (
        <input
            className={`${classes.redeemInput} ${showFieldError(path) ? classes.redeemInputError : ""}`}
            name={path}
            aria-label={label}
            value={getIn(values, path) || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={placeholder}
        />
    );

    const redeemField = (label: string, input: React.ReactNode, path: string) => (
        <div className={classes.section}>
            <label className={classes.label}>{label} <span className={classes.asterisk}>*</span></label>
            {input}
            {errorText(path)}
        </div>
    );

    const whereToRedeem = values.redeemSettings?.whereToRedeem || WhereToRedeemEnum.InPerson;
    const redeemableValue = values.isPrivate ? "private" : values.isAvailableToAllMembers ? "all" : values.memberOnly ? "local" : "anyone";

    return (
        <div className={classes.root} data-testid="cmp-perk-form">
            <div className={classes.header}>
                <h1 className={classes.title}>{titleBase}{memberName ? ` ${C.forText} ${memberName}` : ""}</h1>
                <div className={classes.subtitle}>{C.subtitle}</div>
            </div>

            <form onSubmit={formik.handleSubmit} noValidate>
                <div className={classes.card}>
                    {/* Perk Description */}
                    <div className={classes.section}>
                        <label className={classes.label} htmlFor="cmp-perk-description">{C.perkDescription} <span className={classes.asterisk}>*</span></label>
                        <p className={classes.helper}>{C.perkDescriptionHelper1}</p>
                        <p className={classes.helper}>{C.perkDescriptionHelper2}</p>
                        <div className={classes.fieldBox} style={{ border: fieldBorder(descriptionError, idaGeneratedDescription) }}>
                            {idaGeneratedDescription && <div className={classes.idaLabel}>{idaDescriptionLabel}</div>}
                            <textarea
                                id="cmp-perk-description"
                                className={classes.textarea}
                                placeholder={C.perkDescriptionPlaceholder}
                                name="description"
                                rows={5}
                                maxLength={250}
                                value={values.description}
                                onChange={(e) => { formik.handleChange(e); onDescriptionTyped(); }}
                                onBlur={formik.handleBlur}
                            />
                            {descriptionError && <div data-error="true" className={classes.fieldError}>{descriptionError}</div>}
                        </div>
                        <div className={classes.counterRow}>
                            <span className={classes.counter}>{values.description?.length || 0}/250 {C.characters}</span>
                            {generateControl("description")}
                        </div>
                    </div>

                    {/* Perk Title */}
                    <div className={classes.section}>
                        <label className={classes.label} htmlFor="cmp-perk-title">{C.perkTitle} <span className={classes.asterisk}>*</span></label>
                        <p className={classes.helper}>{C.perkTitleHelper1}</p>
                        <p className={classes.helper}>{C.perkTitleHelper2}</p>
                        <div className={classes.fieldBox} style={{ border: fieldBorder(titleError, idaGeneratedTitle) }}>
                            {idaGeneratedTitle && <div className={classes.idaLabel}>{idaTitleLabel}</div>}
                            <input
                                id="cmp-perk-title"
                                className={classes.textInput}
                                placeholder={C.perkTitlePlaceholder}
                                name="title"
                                maxLength={50}
                                value={values.title}
                                onChange={(e) => { formik.handleChange(e); onTitleTyped(); }}
                                onBlur={formik.handleBlur}
                            />
                            {titleError && <div data-error="true" className={classes.fieldError}>{titleError}</div>}
                        </div>
                        <div className={classes.counterRow}>
                            <span className={classes.counter}>{values.title?.length || 0}/50 {C.characters}</span>
                            {generateControl("title")}
                        </div>
                    </div>

                    {/* Featured Member Perk Banners */}
                    <CmpPerkBannerSection isFeaturedMember={isFeaturedMember} formik={formik} submitLabel={bannerFooterAction} />

                    {/* Perk Redeemable By */}
                    <div className={classes.sectionWide}>
                        <div className={classes.label}>{C.redeemableBy} <span className={classes.asterisk}>*</span></div>
                        <p className={classes.helper}>{C.redeemableByHelperBefore}<b>{C.redeemableByHelperBold}</b>.</p>
                        <RadioGroup
                            className={classes.radioList}
                            aria-label={C.redeemableBy}
                            value={redeemableValue}
                            onChange={(e) => {
                                const val = e.target.value;
                                formik.setFieldValue("isPrivate", val === "private");
                                formik.setFieldValue("isAvailableToAllMembers", val === "all");
                                formik.setFieldValue("memberOnly", val === "local");
                            }}
                        >
                            {radio("anyone", redeemableValue === "anyone", <>{C.anyone}<b>{C.recommended}</b>)</>, C.anyoneDesc)}
                            {radio("local", redeemableValue === "local", C.localMembers, C.localMembersDesc)}
                            {radio("all", redeemableValue === "all", C.allMembers, C.allMembersDesc)}
                            {radio("private", redeemableValue === "private", C.employeesOnly, C.employeesOnlyDesc)}
                        </RadioGroup>
                    </div>

                    {/* Companies Employee ONLY */}
                    {values.isPrivate && (
                        <div className={classes.employeeBox}>
                            <div className={classes.employeePill}>{C.companiesEmployeeOnly}</div>
                            <div className={classes.howToTitle}>{C.howToSetUp}</div>
                            <div className={classes.optionHeading}>{C.option1}</div>
                            <p className={classes.optionText}>{C.option1Text1}</p>
                            <p className={classes.optionText} style={{ marginBottom: "20px" }}>
                                Enter the email address they used to create their account in the <b>Enter Employee Email</b> field below. <b>Press Enter</b> after each email to add it to the list before entering another.
                            </p>
                            <div className={classes.optionHeading}>{C.option2}</div>
                            <p className={classes.optionText}>
                                If you have a list of employee email addresses used to create their personal Chamber Perks App™ accounts, click the <b>Import CSV</b> button to upload a CSV file containing the list.
                            </p>
                            <p className={classes.optionText} style={{ marginBottom: "16px" }}>{C.option2Text2}</p>
                            <div className={classes.csvButtons}>
                                <CSVReader
                                    onUploadAccepted={(results: any) =>
                                        setDataAfterCSVImport(results, (emailsToSet) => formik.setFieldValue("privateEmails", emailsToSet))
                                    }
                                >
                                    {({ getRootProps }: any) => (
                                        <button type="button" className={classes.importCsvButton} {...getRootProps()}>{C.importCsv}</button>
                                    )}
                                </CSVReader>
                                <button type="button" className={classes.sampleButton} onClick={downloadSample}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={CMP.gold600} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M12 4v11M7 11l5 5 5-5" />
                                        <path d="M4 20h16" />
                                    </svg>
                                    {C.downloadSample}
                                </button>
                            </div>
                            <div className={classes.emailSection}>
                                <label className={classes.label} htmlFor="privateEmails">{C.enterEmployeesEmail}</label>
                                <div className={classes.emailBox}>
                                    <Autocomplete<string, true, false, true>
                                        multiple
                                        id="privateEmails"
                                        options={[]}
                                        freeSolo
                                        value={values.privateEmails}
                                        onChange={(_, newValue) => {
                                            setPrivateEmails(newValue, (emailsToSet) => formik.setFieldValue("privateEmails", emailsToSet));
                                        }}
                                        renderTags={(tagValues, getTagProps) =>
                                            tagValues.map((option, index) => {
                                                const tagData = getTagProps({ index });
                                                const { onDelete, ...tagProps } = "onDelete" in tagData ? tagData : { ...tagData, onDelete: undefined };
                                                return (
                                                    <span key={`${option}-${index}`} {...tagProps} className={classes.emailChip}>
                                                        <span className={classes.emailChipText}>{option}</span>
                                                        <button
                                                            type="button"
                                                            aria-label={`Remove ${option}`}
                                                            className={classes.emailChipRemove}
                                                            onClick={(e) => { if (typeof onDelete === "function") onDelete(e); }}
                                                        >✕</button>
                                                    </span>
                                                );
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} variant="outlined" placeholder={C.enterEmployeesEmail} />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={classes.importantNote}>
                                <div className={classes.importantNoteTitle}>{C.importantNote}</div>
                                <div className={classes.importantNoteText}>{C.importantNoteBefore}<b>{C.importantNoteBold}</b></div>
                            </div>
                        </div>
                    )}

                    {/* Perk Redemption Availability */}
                    <div className={classes.sectionWide}>
                        <div className={classes.label}>{C.availability} <span className={classes.asterisk}>*</span></div>
                        <p className={classes.helper}>{C.availabilityHelper}</p>
                        <RadioGroup
                            className={classes.radioGrid}
                            aria-label={C.availability}
                            value={values.isReoccuring ? "reoccuring" : "one-time"}
                            onChange={(e) => formik.setFieldValue("isReoccuring", e.target.value === "reoccuring")}
                        >
                            {radio("reoccuring", !!values.isReoccuring, C.recurring, C.recurringDesc)}
                            {radio("one-time", !values.isReoccuring, C.oneTime, C.oneTimeDesc)}
                        </RadioGroup>
                        {formik.submitCount > 0 && formik.errors.isReoccuring && (
                            <div data-error="true" className={classes.fieldError}>{formik.errors.isReoccuring}</div>
                        )}
                    </div>

                    {/* Only Offer Perk on Certain Days */}
                    <div className={classes.section}>
                        <div className={classes.label}>{C.certainDays}</div>
                        <p className={classes.helper}>{C.certainDaysHelper}</p>
                        {toggle(C.certainDays, !!values.isCertainDaysEnabled, (next) => {
                            formik.setFieldValue("isCertainDaysEnabled", next);
                            if (!next) formik.setFieldValue("daysAvailable", []);
                        })}
                        {values.isCertainDaysEnabled && (
                            <div className={classes.daysBox}>
                                {DAYS.map((day) => {
                                    const selected = !!values.daysAvailable?.includes(day.value);
                                    return (
                                        <button
                                            key={day.value}
                                            type="button"
                                            role="checkbox"
                                            aria-checked={selected}
                                            className={classes.dayOption}
                                            onClick={() => onDayToggle(day.value)}
                                        >
                                            <span className={classes.dayBox} style={{ border: selected ? `2px solid ${CMP.green500}` : `1.5px solid ${CMP.gray400}` }}>
                                                {selected && <CheckMark />}
                                            </span>
                                            <span className={classes.dayName}>{day.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Schedule Perk Display Date */}
                    <div className={classes.section}>
                        <div className={classes.label}>{C.schedule}</div>
                        <p className={classes.helper}>{C.scheduleHelper}</p>
                        {toggle(C.schedule, !!values.isExpiryEnabled, (next) => {
                            formik.setFieldValue("isExpiryEnabled", next);
                            if (!next) {
                                formik.setFieldValue("startDate", "");
                                formik.setFieldValue("endDate", "");
                            }
                        })}
                        {values.isExpiryEnabled && (
                            <div className={classes.dateGrid}>
                                <div className={classes.dateColumn}>
                                    <span className={classes.dateLabel}>{C.startDate}</span>
                                    {datePicker("startDate")}
                                    {formik.submitCount > 0 && formik.errors.startDate && (
                                        <div data-error="true" className={classes.fieldError}>{formik.errors.startDate}</div>
                                    )}
                                </div>
                                <div className={classes.dateColumn}>
                                    <span className={classes.dateLabel}>{C.endDate}</span>
                                    {datePicker("endDate")}
                                    {formik.submitCount > 0 && formik.errors.endDate && (
                                        <div data-error="true" className={classes.fieldError}>{formik.errors.endDate}</div>
                                    )}
                                </div>
                            </div>
                        )}
                        {showFutureStartDateNotification && <div className={classes.darkNote}>{C.notificationNote}</div>}
                    </div>

                    {/* Where to Redeem Perk */}
                    <div className={classes.sectionWide}>
                        <div className={classes.label}>{C.whereToRedeem} <span className={classes.asterisk}>*</span></div>
                        <p className={classes.helperPlain}>{C.whereToRedeemHelper}</p>
                        <RadioGroup
                            className={classes.radioList}
                            aria-label={C.whereToRedeem}
                            value={whereToRedeem}
                            onChange={(e) => formik.setFieldValue("redeemSettings.whereToRedeem", e.target.value)}
                        >
                            {radio(WhereToRedeemEnum.InPerson, whereToRedeem === WhereToRedeemEnum.InPerson, C.inPerson, C.inPersonDesc)}
                            {radio(WhereToRedeemEnum.Online, whereToRedeem === WhereToRedeemEnum.Online, C.online, C.onlineDesc)}
                            {radio(WhereToRedeemEnum.ContactCompany, whereToRedeem === WhereToRedeemEnum.ContactCompany, C.contactCompany, C.contactCompanyDesc)}
                        </RadioGroup>
                        {errorText("redeemSettings.whereToRedeem")}

                        {whereToRedeem === WhereToRedeemEnum.Online && (
                            <div className={classes.redeemWrap}>
                                <div className={classes.redeemPill}>{C.forOnlineBefore}<b>{C.online}</b>{C.forOnlineAfter}</div>
                                <div className={classes.redeemBox}>
                                    {redeemField(C.discountCode, redeemInput("redeemSettings.howToRedeem.code", C.discountCodePlaceholder, C.discountCode), "redeemSettings.howToRedeem.code")}
                                    {redeemField(C.ecommerceUrl, redeemInput("redeemSettings.howToRedeem.websiteToEnterCodeAt", C.ecommerceUrlPlaceholder, C.ecommerceUrl), "redeemSettings.howToRedeem.websiteToEnterCodeAt")}
                                </div>
                            </div>
                        )}

                        {whereToRedeem === WhereToRedeemEnum.ContactCompany && (
                            <div className={classes.redeemWrap}>
                                <div className={classes.redeemPill}>{C.forOnlineBefore}<b>{C.contactCompany}</b>{C.forContactAfter}</div>
                                <div className={classes.redeemBox}>
                                    {redeemField(C.contactName, redeemInput("redeemSettings.howToRedeem.contactName", C.contactNamePlaceholder, C.contactName), "redeemSettings.howToRedeem.contactName")}
                                    {redeemField(C.contactEmail, redeemInput("redeemSettings.howToRedeem.contactEmail", C.contactEmailPlaceholder, C.contactEmail), "redeemSettings.howToRedeem.contactEmail")}
                                    {redeemField(
                                        C.contactNumber,
                                        <NumberFormat
                                            format="(###) ###-####"
                                            aria-label={C.contactNumber}
                                            className={`${classes.redeemInput} ${showFieldError("redeemSettings.howToRedeem.contactNumber") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.contactNumber"
                                            value={values.redeemSettings.howToRedeem?.contactNumber || ""}
                                            onValueChange={(v) => formik.setFieldValue("redeemSettings.howToRedeem.contactNumber", v.value)}
                                            onBlur={formik.handleBlur}
                                            placeholder={C.contactNumberPlaceholder}
                                        />,
                                        "redeemSettings.howToRedeem.contactNumber"
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Send New Perk Notification */}
                    <div className={classes.section}>
                        <div className={classes.label}>{C.sendNotification}</div>
                        <p className={classes.helperPlain}>{C.sendNotificationHelper}</p>
                        {notifyNote && <p className={classes.helperPlain}>{notifyNote}</p>}
                        {toggle(C.sendNotification, !!values.notify, (next) => formik.setFieldValue("notify", next), notifyDisabled)}
                    </div>
                </div>

                <div className={classes.submitRow}>
                    <button type="submit" className={classes.submitButton} disabled={loader}>
                        {loader ? <CircularProgress size={20} style={{ color: "white" }} /> : submitLabel}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CmpPerkForm;
